import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useSiteClub } from "../lib/SiteClubContext";
import { formatEventDate } from "../lib/formatDate";
import {
  createVehicle,
  deleteMedia,
  deleteVehicle,
  getMe,
  getMediaUploadUrl,
  getMyCredits,
  listFields,
  listMyMedia,
  listMyRegistrations,
  listMyVehicles,
  updateMe,
  updateVehicle,
  type ClubCredits,
  type CreditLedgerRow,
  type CustomFields,
  type FieldDefinitionRow,
  type MediaRow,
  type MyRegistrationRow,
  type UserRow,
  type VehicleRow,
} from "../lib/api";
import InfoTip from "../components/InfoTip";
import { NO_REMINDERS_LABEL, PHONE_TIP } from "../lib/notifications";
import { MediaViewer } from "../components/MediaViewer";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

type Tab = "profile" | "vehicles" | "uploads" | "registrations" | "credits";
const TAB_VALUES: Tab[] = ["profile", "vehicles", "uploads", "registrations", "credits"];

function ProfileTab() {
  const [me, setMe] = useState<UserRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    getMe()
      .then((res) => setMe(res.user))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load profile."));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!me) return;
    setError(null);
    setSaving(true);
    try {
      const res = await updateMe({
        name: me.name,
        phone: me.phone ?? "",
        // Unset counts as on, matching the unchecked opt-out box below.
        smsRaceReminders: me.smsRaceReminders !== false,
        bio: me.bio,
        sponsors: me.sponsors,
        socialLinks: me.socialLinks,
        profilePhotoKey: me.profilePhotoKey,
      });
      setMe(res.user);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  // v1 limitation: there's no dedicated backend route for profile photos.
  // We reuse the event-media upload-url flow (POST
  // /events/{eventId}/media/upload-url) with a synthetic "profile" eventId
  // just to get a presigned S3 PUT URL and a unique key under the media
  // bucket, PUT the file directly to S3, and then store the resulting s3Key
  // on the user row via PUT /me -- we deliberately skip the media "confirm"
  // step (POST .../media) since that would create a MediaRow tied to a
  // nonexistent event. If a dedicated profile-photo route is added later,
  // swap this for that.
  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !me) return;
    setUploadingPhoto(true);
    setError(null);
    try {
      const { uploadUrl, s3Key } = await getMediaUploadUrl("profile", file.name, file.type);
      const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!putRes.ok) throw new Error("Upload to storage failed.");
      setMe((prev) => (prev ? { ...prev, profilePhotoKey: s3Key } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Photo upload failed.");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  if (!me) return <p className="muted">Loading…</p>;

  return (
    <form onSubmit={handleSave} className="card">
      {error && <p className="err">{error}</p>}
      {saved && <p className="ok">Saved.</p>}

      <label htmlFor="profile-photo">Profile photo</label>
      {me.profilePhotoKey && (
        <p className="muted" style={{ fontSize: 12 }}>Current: {me.profilePhotoKey}</p>
      )}
      <input id="profile-photo" type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoChange} disabled={uploadingPhoto} />
      {uploadingPhoto && <p className="muted">Uploading…</p>}

      <div style={{ marginTop: "1rem" }}>
        <label htmlFor="profile-email">Email</label>
        <input id="profile-email" value={me.email} disabled title="Set at sign-up -- not editable here" />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="profile-name">Name</label>
        <input id="profile-name" value={me.name ?? ""} onChange={(e) => setMe({ ...me, name: e.target.value })} />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="profile-phone" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
          Phone <InfoTip text={PHONE_TIP} />
        </label>
        <input
          id="profile-phone"
          type="tel"
          autoComplete="tel"
          placeholder="(509) 555-1234"
          value={me.phone ?? ""}
          onChange={(e) => setMe({ ...me, phone: e.target.value })}
        />
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.5rem" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={me.smsRaceReminders === false}
            disabled={!me.phone}
            onChange={(e) => setMe({ ...me, smsRaceReminders: !e.target.checked })}
          />
          {NO_REMINDERS_LABEL}
        </label>
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="profile-bio">Bio</label>
        <textarea id="profile-bio" rows={4} value={me.bio ?? ""} onChange={(e) => setMe({ ...me, bio: e.target.value })} />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="profile-sponsors">Sponsors (comma separated)</label>
        <input
          id="profile-sponsors"
          value={(me.sponsors ?? []).join(", ")}
          onChange={(e) => setMe({ ...me, sponsors: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
        />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="profile-social">Social links (one per line, "label: url")</label>
        <textarea
          id="profile-social"
          rows={3}
          value={Object.entries(me.socialLinks ?? {}).map(([k, v]) => `${k}: ${v}`).join("\n")}
          onChange={(e) => {
            const links: Record<string, string> = {};
            e.target.value.split("\n").forEach((line) => {
              const idx = line.indexOf(":");
              if (idx > 0) links[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
            });
            setMe({ ...me, socialLinks: links });
          }}
        />
      </div>
      <button type="submit" disabled={saving} style={{ marginTop: "1rem" }}>
        {saving ? "Saving…" : "Save profile"}
      </button>
    </form>
  );
}

/** Renders one admin-defined field (text/number/boolean/select -- select is
 *  the "dropdown" an admin manages via the Admin > Field definitions panel)
 *  as a real, editable input bound to a vehicle/user's customFields map. */
function CustomFieldInput({
  def,
  value,
  onChange,
}: {
  def: FieldDefinitionRow;
  value: string | number | boolean | undefined;
  onChange: (value: string | number | boolean) => void;
}) {
  const id = `cf-${def.fieldKey}`;
  if (def.type === "boolean") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <input id={id} type="checkbox" style={{ width: "auto" }} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {def.label}
      </label>
    );
  }
  return (
    <div>
      <label htmlFor={id}>{def.label}{def.required && " *"}</label>
      {def.type === "select" ? (
        <select id={id} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={def.required}>
          <option value="" />
          {(def.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={def.type === "number" ? "number" : "text"}
          value={String(value ?? "")}
          onChange={(e) => onChange(def.type === "number" ? Number(e.target.value) : e.target.value)}
          required={def.required}
        />
      )}
    </div>
  );
}

// Vehicle fields that are built-in columns but get their dropdown choices
// from a "select" field definition with this key (Admin > Field
// definitions). Without one, the field stays free text.
const BUILTIN_VEHICLE_FIELDS = {
  manufacturer: "manufacturer",
  class: "class",
  "radio-manufacturer": "radioManufacturer",
} as const;
type BuiltinVehicleKey = (typeof BUILTIN_VEHICLE_FIELDS)[keyof typeof BUILTIN_VEHICLE_FIELDS];
const isBuiltinKey = (key: string) => key in BUILTIN_VEHICLE_FIELDS;

/** Dropdown when the admin defined choices, text box otherwise. A value
 *  saved before the choices existed (or since removed) stays selectable so
 *  editing an old car doesn't silently clear it. */
function ChoiceInput({
  id,
  value,
  options,
  required,
  onChange,
}: {
  id: string;
  value: string;
  options?: string[];
  required?: boolean;
  onChange: (value: string) => void;
}) {
  if (!options?.length) {
    return <input id={id} value={value} onChange={(e) => onChange(e.target.value)} required={required} />;
  }
  const choices = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} required={required}>
      <option value="">Choose…</option>
      {choices.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function VehiclesTab() {
  const [vehicles, setVehicles] = useState<VehicleRow[] | null>(null);
  const [fieldDefs, setFieldDefs] = useState<FieldDefinitionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<VehicleRow>>({ manufacturer: "", class: "" });
  const [editingId, setEditingId] = useState<string | null>(null);

  function refresh() {
    listMyVehicles()
      .then((res) => setVehicles(res.vehicles))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load vehicles."));
  }
  useEffect(refresh, []);
  const [userFieldDefs, setUserFieldDefs] = useState<FieldDefinitionRow[]>([]);
  useEffect(() => {
    listFields("vehicle").then((res) => setFieldDefs(res.fields)).catch(() => {});
    // Older setups keep the "class" choices under user fields (see the
    // Admin events form), so fall back to those for the built-in keys.
    listFields("user").then((res) => setUserFieldDefs(res.fields)).catch(() => {});
  }, []);

  const choicesFor = (key: keyof typeof BUILTIN_VEHICLE_FIELDS) =>
    (fieldDefs.find((d) => d.fieldKey === key && d.type === "select") ??
      userFieldDefs.find((d) => d.fieldKey === key && d.type === "select"))?.options;
  const customDefs = fieldDefs.filter((d) => !isBuiltinKey(d.fieldKey));
  const setBuiltin = (key: BuiltinVehicleKey, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  function setCustomField(key: string, value: string | number | boolean) {
    setForm((prev) => ({ ...prev, customFields: { ...prev.customFields, [key]: value } }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editingId) {
        await updateVehicle(editingId, form);
      } else {
        await createVehicle(form);
      }
      setForm({ manufacturer: "", class: "" });
      setEditingId(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  async function handleDelete(vehicleId: string) {
    try {
      await deleteVehicle(vehicleId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  function startEdit(v: VehicleRow) {
    setEditingId(v.vehicleId);
    setForm({
      manufacturer: v.manufacturer,
      class: v.class,
      radioManufacturer: v.radioManufacturer,
      transponderNumber: v.transponderNumber,
      customFields: v.customFields,
    });
  }

  return (
    <div>
      {error && <p className="err">{error}</p>}
      <form onSubmit={handleSubmit} className="card">
        <h3>{editingId ? "Edit car" : "Add a car"}</h3>
        <div className="row">
          <div>
            <label htmlFor="v-manufacturer">Manufacturer</label>
            <ChoiceInput id="v-manufacturer" value={form.manufacturer ?? ""} options={choicesFor("manufacturer")} onChange={(v) => setBuiltin("manufacturer", v)} required />
          </div>
          <div>
            <label htmlFor="v-class">Class</label>
            <ChoiceInput id="v-class" value={form.class ?? ""} options={choicesFor("class")} onChange={(v) => setBuiltin("class", v)} required />
          </div>
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor="v-radio">Radio manufacturer</label>
            <ChoiceInput id="v-radio" value={form.radioManufacturer ?? ""} options={choicesFor("radio-manufacturer")} onChange={(v) => setBuiltin("radioManufacturer", v)} />
          </div>
          <div>
            <label htmlFor="v-transponder">Transponder #</label>
            <input id="v-transponder" value={form.transponderNumber ?? ""} onChange={(e) => setForm({ ...form, transponderNumber: e.target.value })} />
          </div>
        </div>
        {customDefs.length > 0 && (
          <div className="row" style={{ marginTop: "0.75rem" }}>
            {customDefs.map((def) => (
              <CustomFieldInput
                key={def.fieldKey}
                def={def}
                value={form.customFields?.[def.fieldKey]}
                onChange={(v) => setCustomField(def.fieldKey, v)}
              />
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button type="submit">{editingId ? "Save changes" : "Add car"}</button>
          {editingId && (
            <button type="button" className="secondary" onClick={() => { setEditingId(null); setForm({ manufacturer: "", class: "" }); }}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {vehicles?.map((v) => (
        <div className="vehicle-card" key={v.vehicleId}>
          <div>
            <strong>{v.manufacturer}</strong> — {v.class}
            {v.radioManufacturer && <div className="muted">Radio: {v.radioManufacturer}</div>}
            {v.transponderNumber && <div className="muted">Transponder: {v.transponderNumber}</div>}
            {v.customFields && Object.keys(v.customFields).length > 0 && (
              <div className="muted">
                {customDefs.map((def) =>
                  v.customFields && def.fieldKey in v.customFields ? (
                    <div key={def.fieldKey}>
                      {def.label}: {String(v.customFields[def.fieldKey])}
                    </div>
                  ) : null,
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="secondary" onClick={() => startEdit(v)}>Edit</button>
            <button className="danger" onClick={() => handleDelete(v.vehicleId)}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function UploadsTab() {
  const { club, loading: clubLoading } = useSiteClub();
  const [media, setMedia] = useState<MediaRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<MediaRow | null>(null);

  // On a club's domain, only uploads from that club's events.
  function refresh() {
    if (clubLoading) return;
    listMyMedia(club?.clubId)
      .then((res) => setMedia(res.media))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load uploads."));
  }
  useEffect(refresh, [club?.clubId, clubLoading]);

  const eventTitles: Record<string, string> = Object.fromEntries(
    (media ?? []).filter((m) => m.eventTitle).map((m) => [m.eventId, m.eventTitle!]),
  );

  async function handleDelete(m: MediaRow) {
    try {
      await deleteMedia(m.eventId, m.mediaId);
      setMedia((prev) => prev?.filter((x) => x.mediaId !== m.mediaId) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  const groups = new Map<string, MediaRow[]>();
  for (const m of media ?? []) {
    const list = groups.get(m.eventId);
    if (list) list.push(m);
    else groups.set(m.eventId, [m]);
  }
  const groupedEntries = [...groups.entries()].sort(
    (a, b) => b[1][0].createdAt.localeCompare(a[1][0].createdAt),
  );

  return (
    <div>
      {error && <p className="err">{error}</p>}
      {groupedEntries.map(([eventId, items]) => (
        <div key={eventId} style={{ marginBottom: "1.5rem" }}>
          <h3>
            <Link to={`/events/${eventId}`}>{eventTitles[eventId] ?? eventId}</Link>
          </h3>
          <div className="media-grid">
            {items.map((m) => (
              <div className="media-item" key={m.mediaId}>
                {m.type === "video" ? (
                  <video src={m.url ?? m.s3Key} muted preload="metadata" onClick={() => setViewing(m)} />
                ) : (
                  <img src={m.url ?? m.s3Key} alt={m.caption ?? ""} onClick={() => setViewing(m)} />
                )}
                <button className="danger media-delete" onClick={() => handleDelete(m)}>Delete</button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {media && media.length === 0 && (
        <p className="muted">{club ? `No uploads from ${club.name} events yet.` : "No uploads yet."}</p>
      )}
      {viewing && (
        <MediaViewer
          url={viewing.url ?? viewing.s3Key}
          name={viewing.caption ?? "Media"}
          type={viewing.type}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/** Every race this person is registered for, across all clubs -- unlike
 *  Uploads, this isn't scoped to the club whose site you're on. Events from
 *  another club link to that club's own domain. */
function RegistrationsTab() {
  const { club: siteClub } = useSiteClub();
  const [registrations, setRegistrations] = useState<MyRegistrationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMyRegistrations()
      .then((res) => setRegistrations(res.registrations))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load registrations."));
  }, []);

  function eventLink(r: MyRegistrationRow) {
    const title = r.eventTitle ?? r.eventId;
    const elsewhere = r.clubDomain && r.clubId !== siteClub?.clubId && r.clubDomain !== window.location.hostname;
    return elsewhere ? (
      <a href={`https://${r.clubDomain}/events/${encodeURIComponent(r.eventId)}`}>{title}</a>
    ) : (
      <Link to={`/events/${encodeURIComponent(r.eventId)}`}>{title}</Link>
    );
  }

  return (
    <div>
      {error && <p className="err">{error}</p>}
      <div className="table-scroll"><table>
        <thead>
          <tr>
            <th>Event</th>
            <th>Date</th>
            <th>Club</th>
            <th>Racer</th>
            <th>Class</th>
            <th>Status</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {registrations?.map((r) => (
            <tr key={r.registrationId}>
              <td>{eventLink(r)}</td>
              <td>{r.eventDateISO ? formatEventDate(r.eventDateISO, r.eventEndDateISO) : ""}</td>
              <td>{r.clubName ?? ""}</td>
              <td>{r.racerName}</td>
              <td>{r.racerClass}</td>
              <td><span className={`pill ${r.status === "paid" ? "success" : r.status === "cancelled" || r.status === "refunded" ? "danger" : "warn"}`}>{r.status}</span></td>
              <td>{formatDollars(r.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {registrations && registrations.length === 0 && <p className="muted">No registrations yet.</p>}
    </div>
  );
}

/** Credits are per club: each club's balance can only be spent on that
 *  club's events. On a club's own domain, that club is listed first. */
function CreditsTab() {
  const { club: siteClub } = useSiteClub();
  const [clubs, setClubs] = useState<ClubCredits[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMyCredits()
      .then((res) => setClubs(res.clubs))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load credits."));
  }, []);

  const ordered = [...(clubs ?? [])].sort(
    (a, b) => Number(b.clubId === siteClub?.clubId) - Number(a.clubId === siteClub?.clubId),
  );

  return (
    <div>
      {error && <p className="err">{error}</p>}
      {ordered.map((c) => (
        <div key={c.clubId} style={{ marginBottom: "1.5rem" }}>
          <h3>
            {c.clubName}: {formatDollars(c.balanceCents)}
          </h3>
          {c.clubId === "unassigned" && (
            <p className="muted" style={{ fontSize: 13 }}>
              Older credits that aren't tied to a club yet. Ask a club admin to move them to your club.
            </p>
          )}
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {c.ledger.map((l: CreditLedgerRow) => (
                <tr key={l.txnId}>
                  <td>{new Date(l.createdAt).toLocaleDateString()}</td>
                  <td>{l.amountCents >= 0 ? "+" : ""}{formatDollars(l.amountCents)}</td>
                  <td>{l.reason}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      ))}
      {clubs && clubs.length === 0 && <p className="muted">No credit history yet.</p>}
      {clubs && clubs.length > 0 && (
        <p className="muted" style={{ fontSize: 13 }}>Each club's credits can only be used on that club's events.</p>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const initialTab: Tab = TAB_VALUES.includes(requestedTab as Tab) ? (requestedTab as Tab) : "profile";
  const [tab, setTab] = useState<Tab>(initialTab);

  // Picks up a tab switch from the nav dropdown even when it doesn't
  // remount this page (already on /account, just a different ?tab=).
  useEffect(() => {
    if (requestedTab && TAB_VALUES.includes(requestedTab as Tab)) setTab(requestedTab as Tab);
  }, [requestedTab]);

  // "uploads" isn't gated -- event media upload is open to any signed-in
  // member (see EventDetailPage.tsx), so viewing your own uploads can't be
  // verified-only either, or an unverified member could upload but never
  // see what they uploaded.
  const verifiedOnlyTabs: Tab[] = ["vehicles", "registrations", "credits"];
  const needsVerification = verifiedOnlyTabs.includes(tab) && !user?.isVerifiedMember;

  return (
    <div>
      <h1>Account</h1>
      <div className="tabs">
        <button className={tab === "profile" ? "active" : ""} onClick={() => setTab("profile")}>Profile</button>
        <button className={tab === "vehicles" ? "active" : ""} onClick={() => setTab("vehicles")}>My cars</button>
        <button className={tab === "uploads" ? "active" : ""} onClick={() => setTab("uploads")}>My uploads</button>
        <button className={tab === "registrations" ? "active" : ""} onClick={() => setTab("registrations")}>My registrations</button>
        <button className={tab === "credits" ? "active" : ""} onClick={() => setTab("credits")}>My credits</button>
      </div>

      {needsVerification ? (
        <p className="notice">Your account is pending verification by a club admin.</p>
      ) : (
        <>
          {tab === "profile" && <ProfileTab />}
          {tab === "vehicles" && <VehiclesTab />}
          {tab === "uploads" && <UploadsTab />}
          {tab === "registrations" && <RegistrationsTab />}
          {tab === "credits" && <CreditsTab />}
        </>
      )}
    </div>
  );
}
